import {useEffect, useState, useRef, useCallback} from "react";
import {useParams} from "react-router-dom";
import {useSelector, useDispatch, useStore} from "react-redux";
import {PowerBIEmbed} from "powerbi-client-react";
import {models} from "powerbi-client";

import PowerBILoader from "./PowerBILoader";
import BottomBar from "./BottomBar";
import injectPowerBIStyles from "./injectPowerBIStyles";
import {ZOOM_STEP, ZOOM_MIN, ZOOM_MAX} from "./constants";
import {REPORT_IDs} from "../../utils/LS-CONSTANT";
import LS_API from "../../api/axios";
import logger from "../../utils/logger";
import {dashboardAction} from "../../store/slices/dashboard";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getAccessToken = () => {
    const match = document.cookie.match(/(^| )access_token=([^;]+)/);
    if (match) return match[2];

    const cognitoKey = Object.keys(localStorage).find(
        (k) =>
            k.startsWith("CognitoIdentityServiceProvider.") &&
            k.endsWith(".accessToken")
    );
    if (cognitoKey) return localStorage.getItem(cognitoKey);

    return null;
};

const reportIdMap = {
    ccmr: REPORT_IDs.ccmr,
    dq: REPORT_IDs.dq,
    "ccmr-sla": REPORT_IDs["ccmr-sla"],
    "operational-dashboard": REPORT_IDs["operational-dashboard"],
    "client-dashboard": REPORT_IDs["client-dashboard"]
};

const DISPLAY_OPTION_MAP = {
    fitToWidth: models.DisplayOption.FitToWidth,
    fitToPage:  models.DisplayOption.FitToPage,
    actualSize: models.DisplayOption.ActualSize,
};

// ─── Apply settings helper ────────────────────────────────────────────────────
// IMPORTANT: setZoom only works when displayMode is "actualSize".
// For fitToWidth / fitToPage, PowerBI controls zoom itself — setZoom is ignored.
// So the correct sequence is:
//   1. Switch to actualSize first
//   2. Apply zoom
//   3. If user actually wants fitToWidth/fitToPage, switch back — zoom won't apply
//      but at least displayMode is correct.
// When zoom !== 1 and displayMode !== actualSize, we force actualSize so zoom is respected.

const applySettingsToInstance = async (instance, zoom, displayMode) => {
    const needsCustomZoom = zoom !== 1;

    // If user has a custom zoom, we must use actualSize — otherwise zoom is ignored
    const effectiveDisplayMode = needsCustomZoom ? "actualSize" : displayMode;

    // Step 1: set display mode (must come before setZoom)
    await instance.updateSettings({
        layoutType:   models.LayoutType.Custom,
        customLayout: { displayOption: DISPLAY_OPTION_MAP[effectiveDisplayMode] },
    });

    // Step 2: apply zoom (only takes effect under actualSize)
    if (needsCustomZoom) {
        await instance.setZoom(zoom);
    }
};

// ─── Component ────────────────────────────────────────────────────────────────

const PowerBIReport = () => {
    const dispatch = useDispatch();
    const store    = useStore(); // stable reference, getState() always current
    const {zenView} = useSelector((state) => state.appUi);

    const {report} = useParams();
    const activeReportId = reportIdMap[report];

    // ── Global settings (shared across all reports) ──────────────────────────
    const zoom        = useSelector((s) => s.dashboard.zoom);
    const displayMode = useSelector((s) => s.dashboard.displayMode);

    // ── Local UI state ───────────────────────────────────────────────────────
    const [embedConfig, setEmbedConfig] = useState(null);
    const [pages,       setPages]       = useState([]);
    const [activePage,  setActivePage]  = useState(null);
    const [isNavigating, setIsNavigating] = useState(false);
    const [isReady,      setIsReady]      = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // ── Refs ─────────────────────────────────────────────────────────────────
    const embeddedReportRef   = useRef(null);
    const containerRef        = useRef(null);
    const settingsRestoredRef = useRef(false);

    // ── Inject global iframe styles once ─────────────────────────────────────
    useEffect(() => { injectPowerBIStyles(); }, []);

    // ── Load embed config on report change ───────────────────────────────────
    useEffect(() => {
        if (!activeReportId) return;

        settingsRestoredRef.current = false;
        setEmbedConfig(null);
        setPages([]);
        setActivePage(null);
        setIsReady(false);
        embeddedReportRef.current = null;

        const loadReport = async () => {
            try {
                const token = getAccessToken();
                if (!token) return;

                const res = await LS_API.get(`/api/report/embed?reportId=${activeReportId}`);
                const {embedReports, embedToken} = res.data;
                if (!embedReports?.length) return;

                const {embedUrl, reportId: id} = embedReports[0];

                setEmbedConfig({
                    type:        "report",
                    id,
                    embedUrl,
                    accessToken: embedToken,
                    tokenType:   models.TokenType.Embed,
                    settings: {
                        localeSettings: {language: "en", formatLocale: "en-US"},
                        panes: {
                            filters:        {visible: false},
                            pageNavigation: {visible: false},
                        },
                        // Always start with actualSize so the initial zoom can be applied.
                        // applySettingsToInstance will switch to the correct mode after.
                        layoutType:   models.LayoutType.Custom,
                        customLayout: {
                            displayOption: models.DisplayOption.ActualSize,
                            pagesLayout:   {},
                        },
                        background: models.BackgroundType.Default,
                    },
                });
            } catch (err) {
                logger.error("Failed to load report:", err);
            }
        };

        loadReport();
    }, [activeReportId]);

    // ── Toolbar handlers ─────────────────────────────────────────────────────

    const handleZoomOut = useCallback(async () => {
        if (!embeddedReportRef.current) return;
        const next = Math.max(+(zoom - ZOOM_STEP).toFixed(1), ZOOM_MIN);
        try {
            // Zoom requires actualSize — switch first, then zoom
            await embeddedReportRef.current.updateSettings({
                layoutType:   models.LayoutType.Custom,
                customLayout: { displayOption: models.DisplayOption.ActualSize },
            });
            await embeddedReportRef.current.setZoom(next);
            dispatch(dashboardAction.setZoom(next));
            // If displayMode was something else, update it to actualSize in store too
            dispatch(dashboardAction.setDisplayMode("actualSize"));
        } catch (err) {
            logger.error("Zoom out failed:", err);
        }
    }, [zoom, dispatch]);

    const handleZoomIn = useCallback(async () => {
        if (!embeddedReportRef.current) return;
        const next = Math.min(+(zoom + ZOOM_STEP).toFixed(1), ZOOM_MAX);
        try {
            await embeddedReportRef.current.updateSettings({
                layoutType:   models.LayoutType.Custom,
                customLayout: { displayOption: models.DisplayOption.ActualSize },
            });
            await embeddedReportRef.current.setZoom(next);
            dispatch(dashboardAction.setZoom(next));
            dispatch(dashboardAction.setDisplayMode("actualSize"));
        } catch (err) {
            logger.error("Zoom in failed:", err);
        }
    }, [zoom, dispatch]);

    const handleDisplayMode = useCallback(async (mode) => {
        if (!embeddedReportRef.current) return;
        try {
            await embeddedReportRef.current.updateSettings({
                layoutType:   models.LayoutType.Custom,
                customLayout: { displayOption: DISPLAY_OPTION_MAP[mode] },
            });
            dispatch(dashboardAction.setDisplayMode(mode));
            // fitToWidth/fitToPage override zoom — reset zoom to 1 in store
            if (mode !== "actualSize") {
                dispatch(dashboardAction.setZoom(1));
            }
        } catch (err) {
            logger.error("Layout switch failed:", err);
        }
    }, [dispatch]);

    const handleRefresh = useCallback(async () => {
        if (!embeddedReportRef.current || isRefreshing) return;
        setIsRefreshing(true);
        try {
            await embeddedReportRef.current.refresh();
        } catch (err) {
            logger.error("Refresh failed:", err);
        } finally {
            setTimeout(() => setIsRefreshing(false), 1500);
        }
    }, [isRefreshing]);

    const handlePrint = useCallback(async () => {
        if (!embeddedReportRef.current) return;
        try {
            await embeddedReportRef.current.print();
        } catch (err) {
            logger.error("Print failed:", err);
        }
    }, []);

    const handleFullscreen = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;
        if (!document.fullscreenElement) {
            if (el.requestFullscreen)            el.requestFullscreen();
            else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
        } else {
            if (document.exitFullscreen)            document.exitFullscreen();
            else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        }
    }, []);

    // ── Page navigation ──────────────────────────────────────────────────────

    const handlePageChange = async (pageName) => {
        if (!embeddedReportRef.current || pageName === activePage || isNavigating) return;
        setIsNavigating(true);
        try {
            const reportPages = await embeddedReportRef.current.getPages();
            const target = reportPages.find((p) => p.name === pageName);
            if (target) {
                await target.setActive();
                setActivePage(pageName);
            }
        } catch (err) {
            logger.error("Page navigation failed:", err);
        } finally {
            setIsNavigating(false);
        }
    };

    // ── Capture embed instance & wire up events ──────────────────────────────

    const handleEmbeddedComponent = (instance) => {
        embeddedReportRef.current = instance;

        instance.on("loaded", async () => {
            try {
                const reportPages  = await instance.getPages();
                const visiblePages = reportPages.filter((p) => p.visibility === 0);
                setPages(visiblePages);
                if (visiblePages.length > 0) setActivePage(visiblePages[0].name);
            } catch (err) {
                logger.error("Failed to fetch pages:", err);
            }
        });

        instance.on("rendered", async () => {
            // Guard: only restore once per report load, not on every re-render
            if (settingsRestoredRef.current) return;
            settingsRestoredRef.current = true;

            try {
                // Read directly from store — synchronous, never stale
                const state      = store.getState();
                const targetZoom = state.dashboard.zoom;
                const targetMode = state.dashboard.displayMode;

                await applySettingsToInstance(instance, targetZoom, targetMode);

                setIsReady(true);
            } catch (err) {
                logger.error("Failed to apply settings on render:", err);
            }
        });

        instance.on("pageChanged", (event) => {
            const newPage = event?.detail?.newPage;
            if (newPage?.name) setActivePage(newPage.name);
        });
    };

    // ── Layout ───────────────────────────────────────────────────────────────

    const showBar         = zenView || pages.length > 1;
    const bottomBarHeight = showBar ? 40 : 0;

    // ── Render ────────────────────────────────────────────────────────────────

    if (!embedConfig) return <PowerBILoader/>;

    return (
        <div
            ref={containerRef}
            style={{
                height:          "100vh",
                width:           "100%",
                display:         "flex",
                flexDirection:   "column",
                padding:         0,
                margin:          0,
                overflow:        "hidden",
                boxSizing:       "border-box",
                backgroundColor: "#ffffff",
                minHeight:       0,
            }}
        >
            {/* ── Report canvas ── */}
            <div
                style={{
                    flex:          "1 1 0%",
                    minHeight:     0,
                    minWidth:      0,
                    width:         "100%",
                    overflow:      "hidden",
                    display:       "flex",
                    flexDirection: "column",
                    boxSizing:     "border-box",
                    maxHeight:     `calc(100vh - ${bottomBarHeight}px)`,
                }}
            >
                <PowerBIEmbed
                    embedConfig={embedConfig}
                    cssClassName="powerbi-report"
                    getEmbeddedComponent={handleEmbeddedComponent}
                />
            </div>

            {/* ── Bottom bar (pages + toolbar) ── */}
            <BottomBar
                pages={pages}
                activePage={activePage}
                isNavigating={isNavigating}
                onPageChange={handlePageChange}
                isReady={isReady}
                zoom={zoom}
                displayMode={displayMode}
                isRefreshing={isRefreshing}
                onZoomOut={handleZoomOut}
                onZoomIn={handleZoomIn}
                onRefresh={handleRefresh}
                onPrint={handlePrint}
                onFullscreen={handleFullscreen}
                onDisplayMode={handleDisplayMode}
                zenView={zenView}
            />
        </div>
    );
};

export default PowerBIReport;